import { useState } from 'react'
import { AxiosError } from 'axios'
import { Loader2, Pencil } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/shared/components/ui/alert-dialog'
import {
  CHAT_COPY,
  chatErrorMessage,
  chatStatus,
  useAddGroupMembers,
  useChatUserId,
  useConversation,
  useLeaveGroup,
  useRemoveGroupMember,
  useRenameGroup,
} from '@/entities/chat'
import { parseValidationErrors } from '@/shared/api/errorHandling'
import { PersonPicker } from './PersonPicker'
import { PersonAvatar } from './PersonAvatar'

const GROUP_CAPACITY = 50

export function GroupMembersDialog({
  conversationId,
  open,
  onOpenChange,
  onLeft,
}: {
  conversationId: number
  open: boolean
  onOpenChange: (open: boolean) => void
  onLeft: () => void
}) {
  const myUserId = useChatUserId()
  const detailQuery = useConversation(open ? conversationId : null)

  const [renaming, setRenaming] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [renameError, setRenameError] = useState<string | null>(null)
  const [removeErrors, setRemoveErrors] = useState<Record<number, string>>({})
  const [addError, setAddError] = useState<string | null>(null)
  const [leaveOpen, setLeaveOpen] = useState(false)
  const [leaveError, setLeaveError] = useState<string | null>(null)

  const renameGroup = useRenameGroup()
  const removeMember = useRemoveGroupMember()
  const addMembers = useAddGroupMembers()
  const leaveGroup = useLeaveGroup()

  const detail = detailQuery.data

  const startRename = () => {
    if (!detail) return
    setTitleDraft(detail.summary.title)
    setRenameError(null)
    setRenaming(true)
  }

  const saveRename = () => {
    const trimmed = titleDraft.trim()
    if (trimmed === '') {
      setRenameError(CHAT_COPY.groupNeedsName)
      return
    }
    setRenameError(null)
    renameGroup.mutate(
      { id: conversationId, title: trimmed },
      {
        onSuccess: () => setRenaming(false),
        onError: (error) => setRenameError(chatErrorMessage(error, CHAT_COPY.groupRenameFailed)),
      },
    )
  }

  const handleRemove = (userId: number) => {
    setRemoveErrors((current) => {
      const next = { ...current }
      delete next[userId]
      return next
    })
    removeMember.mutate(
      { id: conversationId, userId },
      {
        onError: (error) =>
          setRemoveErrors((current) => ({
            ...current,
            [userId]:
              chatStatus(error) === 404 ? CHAT_COPY.groupRemoveNotFound : chatErrorMessage(error, CHAT_COPY.groupRemoveFailed),
          })),
      },
    )
  }

  const handleAdd = (entry: { userId: number }) => {
    setAddError(null)
    addMembers.mutate(
      { id: conversationId, request: { userIds: [entry.userId] } },
      {
        onError: (error) => {
          const isDuplicate =
            error instanceof AxiosError &&
            parseValidationErrors(error.response?.data).some((problem) => problem.errorCode === 'DUPLICATE_RECORD')
          setAddError(isDuplicate ? CHAT_COPY.groupAddDuplicate : chatErrorMessage(error, CHAT_COPY.groupAddFailed))
        },
      },
    )
  }

  const handleLeave = () => {
    setLeaveError(null)
    leaveGroup.mutate(
      { id: conversationId },
      {
        onSuccess: () => {
          setLeaveOpen(false)
          onOpenChange(false)
          onLeft()
        },
        onError: (error) => setLeaveError(chatErrorMessage(error, CHAT_COPY.groupLeaveFailed)),
      },
    )
  }

  const members = detail?.members ?? []
  const memberIds = members
    .map((member) => member.userId)
    .filter((id): id is number => id != null)
  const canManage = detail?.canManage ?? false
  const atCapacity = members.length >= GROUP_CAPACITY

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{CHAT_COPY.groupMembers}</DialogTitle>
          </DialogHeader>

          {detailQuery.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-ink-4" />
            </div>
          ) : detailQuery.isError || !detail ? (
            <div className="flex flex-col items-center gap-2 py-6 text-center">
              <p className="text-[13px] text-ink-2">{CHAT_COPY.threadFailed}</p>
              <Button variant="outline" size="sm" onClick={() => detailQuery.refetch()}>
                {CHAT_COPY.retry}
              </Button>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                {renaming ? (
                  <div className="flex items-center gap-2">
                    <Input
                      autoFocus
                      value={titleDraft}
                      maxLength={120}
                      onChange={(event) => setTitleDraft(event.target.value)}
                    />
                    <Button size="sm" disabled={renameGroup.isPending} onClick={saveRename}>
                      {CHAT_COPY.groupRename}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 truncate text-[14px] font-semibold text-ink">{detail.summary.title}</p>
                    {canManage && (
                      <Button variant="ghost" size="icon-sm" aria-label={CHAT_COPY.groupRename} onClick={startRename}>
                        <Pencil size={14} />
                      </Button>
                    )}
                  </div>
                )}
                {renameError && <p className="text-[12px] text-bad">{renameError}</p>}
              </div>

              <div className="flex flex-col gap-1.5">
                <p className="text-[12.5px] font-medium text-ink-2">{CHAT_COPY.groupCapacity(members.length)}</p>
                <div className="flex max-h-[200px] flex-col gap-0.5 overflow-y-auto">
                  {members.map((member) => {
                    const isMe = member.userId != null && member.userId === myUserId
                    return (
                      <div key={member.userId ?? member.name} className="flex flex-col gap-0.5 py-1">
                        <div className="flex items-center gap-2">
                          <PersonAvatar
                            name={member.name}
                            photoUrl={member.photoUrl}
                            photoFileId={member.photoFileId}
                            size="sm"
                          />
                          <span className="flex-1 truncate text-[13px] text-ink">
                            {member.name}
                            {isMe && <span className="ml-1.5 text-[11.5px] text-ink-4">{CHAT_COPY.groupYou}</span>}
                          </span>
                          {!isMe && canManage && member.userId != null && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={removeMember.isPending}
                              onClick={() => handleRemove(member.userId as number)}
                            >
                              {CHAT_COPY.groupRemove}
                            </Button>
                          )}
                        </div>
                        {member.userId != null && removeErrors[member.userId] && (
                          <p className="text-[12px] text-bad">{removeErrors[member.userId]}</p>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <p className="text-[12.5px] font-medium text-ink-2">{CHAT_COPY.groupAddPeople}</p>
                {atCapacity ? (
                  <p className="text-[12.5px] text-ink-3">{CHAT_COPY.groupAtCapacity}</p>
                ) : (
                  <PersonPicker
                    selectedIds={[]}
                    excludedIds={memberIds}
                    onPick={handleAdd}
                    autoFocus
                    disabled={addMembers.isPending}
                    pendingUserId={addMembers.isPending ? (addMembers.variables?.request.userIds[0] ?? null) : null}
                  />
                )}
                {addError && <p className="text-[12px] text-bad">{addError}</p>}
              </div>

              <div className="flex justify-end border-t border-line pt-3">
                <Button variant="destructive" size="sm" onClick={() => setLeaveOpen(true)}>
                  {CHAT_COPY.groupLeave}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{CHAT_COPY.groupLeaveConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>{CHAT_COPY.groupLeaveConfirmBody}</AlertDialogDescription>
          </AlertDialogHeader>
          {leaveError && <p className="text-[12.5px] text-bad">{leaveError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={leaveGroup.isPending}>{CHAT_COPY.deleteCancel}</AlertDialogCancel>
            <AlertDialogAction
              disabled={leaveGroup.isPending}
              onClick={(event) => {
                event.preventDefault()
                handleLeave()
              }}
            >
              {CHAT_COPY.groupLeaveAction}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
